import {
    Button,
    Card,
    Input,
    Popover,
    PopoverContent,
    PopoverTrigger,
    ScrollArea,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@repo/shadcn';
import { useEffect, useRef, useState } from 'react';
import { BiMinus, BiPlus } from 'react-icons/bi';
import { TbTrash } from 'react-icons/tb';

import EmptySection from '@/components/ui/empty/empty-section';
import { type CartListItem } from '@/features/slice/cart/cart.schema';

import { formatCurrencyInBDT } from '../../../../utils/currency-formater';
import { Spinner } from '../../../ui/loader/spinner';
import { useCreateSale } from './create-sale.hook';

interface CartListProps {
    cartDataLoading: boolean;
    cartData: CartListItem[];
    isFullscreen: boolean;
}

function CartList({ cartData, cartDataLoading, isFullscreen }: CartListProps) {
    const {
        handleRemoveCartItem,
        updateCartSingleItem,
        handleChangeQuantity,
        removeItemLoading,
        removeId,
    } = useCreateSale();

    if (cartDataLoading) {
        return 'Data  is not available';
    }
    if (!cartData.length) {
        return <EmptySection />;
    } else {
        return (
            <Card className="relative w-full bg-white">
                <>
                    <ScrollArea
                        className={`${isFullscreen ? 'max-h-[calc(100vh-300px)]' : ''}`}
                    >
                        <Table className="relative">
                            <TableHeader className="sticky top-0 z-10 h-[50px] bg-slate-50">
                                <TableRow>
                                    <TableHead>Medicine</TableHead>
                                    <TableHead>Price</TableHead>
                                    <TableHead>Quantity</TableHead>
                                    <TableHead>Discount</TableHead>
                                    <TableHead>Total</TableHead>
                                    <TableHead>Actions</TableHead>
                                </TableRow>
                            </TableHeader>

                            <TableBody>
                                {cartData.map((cart: CartListItem) => (
                                    <TableRow key={cart.id}>
                                        <TableCell>
                                            {' '}
                                            {cart.product_name}
                                        </TableCell>
                                        <TableCell className="w-[100px]">
                                            {' '}
                                            {formatCurrencyInBDT(cart.price)}
                                        </TableCell>
                                        <TableCell>
                                            <div className="flex items-center space-x-2">
                                                <EdtiDiscountPopover
                                                    cart={cart}
                                                    updateCartSingleItem={
                                                        updateCartSingleItem
                                                    }
                                                    handleChangeQuantity={
                                                        handleChangeQuantity
                                                    }
                                                />
                                            </div>
                                        </TableCell>
                                        <TableCell>
                                            <div className="flex items-center space-x-2">
                                                {cart.discount_value}{' '}
                                                {cart.discount_type === 'fixed'
                                                    ? ' tk. '
                                                    : ' %'}
                                            </div>
                                        </TableCell>
                                        <TableCell className="white-space-nowrap w-[100px] font-semibold">
                                            {formatCurrencyInBDT(
                                                cart.total_price,
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            <Button
                                                variant="destructive"
                                                size="icon"
                                                type="button"
                                                onClick={() =>
                                                    handleRemoveCartItem(
                                                        cart.id,
                                                    )
                                                }
                                                className="group bg-red-300 !p-1"
                                            >
                                                {removeItemLoading &&
                                                removeId == cart.id ? (
                                                    <Spinner />
                                                ) : (
                                                    <TbTrash className="h-5 w-5 text-red-800 group-hover:text-white" />
                                                )}
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </ScrollArea>
                </>
            </Card>
        );
    }
}

const EdtiDiscountPopover = ({
    cart,
    updateCartSingleItem,
    handleChangeQuantity,
}: {
    cart: CartListItem;
    updateCartSingleItem: any;
    handleChangeQuantity: any;
}) => {
    const [quantity, setQuantity] = useState(1);
    const inputRef = useRef<HTMLInputElement>(null);
    const handlePopoverOpen = () => {
        if (inputRef.current) {
            inputRef.current.focus();
            inputRef.current.select();
        }
    };
    useEffect(() => {
        // if (quantity < 1)
        //  return setQuantity(1);

        setQuantity(cart.quantity);
    }, [cart.quantity]);

    return (
        <Popover>
            <PopoverTrigger asChild>
                <Button variant="outline">
                    <span>{cart.quantity}</span>
                </Button>
            </PopoverTrigger>
            <PopoverContent
                onClick={handlePopoverOpen}
                className="w-fit space-y-2"
            >
                <div className="flex gap-2">
                    <Button
                        type="button"
                        disabled={quantity < 2}
                        className="border-[1px] bg-secondary text-primary shadow-sm disabled:bg-slate-400 disabled:text-gray-600"
                        onClick={() =>
                            updateCartSingleItem({
                                ...cart,
                                id: cart.id,
                                quantity: cart.quantity - 1,
                            })
                        }
                    >
                        <BiMinus />
                    </Button>
                    <Input
                        ref={inputRef}
                        id="quantity"
                        className="focus-visible:right- w-14 cursor-pointer text-center"
                        value={quantity}
                        onChange={(e) => {
                            setQuantity(
                                Number((e.target as HTMLInputElement).value) ==
                                    0
                                    ? 1
                                    : Number(
                                          (e.target as HTMLInputElement).value,
                                      ),
                            ),
                                handleChangeQuantity({
                                    ...cart,
                                    quantity: (e.target as HTMLInputElement)
                                        .value,
                                });
                        }}
                    />
                    <Button
                        type="button"
                        className="border-[1px] bg-secondary text-primary shadow-sm"
                        onClick={() =>
                            updateCartSingleItem({
                                ...cart,
                                id: cart.id,
                                quantity: cart.quantity + 1,
                            })
                        }
                    >
                        <BiPlus />
                    </Button>
                </div>
            </PopoverContent>
        </Popover>
    );
};

export default CartList;
